import { useDeferredValue, useEffect, useMemo, useRef, useState } from "react";
import {
  ArrowUpRight,
  BadgeCheck,
  Check,
  Filter,
  ImageOff,
  Layers3,
  Search,
  ShoppingBag,
  Sparkles,
  X,
} from "lucide-react";
import type { PublicStorefrontData } from "../data/repository";
import { loadPublicStorefront, recordPublicClick } from "../data/repository";
import { money } from "../lib/format";

// O tamanho da peça vem no fim do nome, no padrão "…, tam. M". Na tela ele sai
// do título e vira etiqueta, ao lado da loja e da categoria, que é onde a pessoa
// procura essa informação.
function separarTamanho(nomeCompleto: string) {
  const achado = nomeCompleto.match(/,\s*tam\.\s*([\p{L}\p{N}]+)\s*$/iu);
  if (!achado) return { nome: nomeCompleto, tamanho: "" };
  return {
    nome: nomeCompleto.slice(0, achado.index).trim(),
    tamanho: achado[1].toUpperCase(),
  };
}

function sessionId() {
  const key = "wesell-public-session";
  const existing = sessionStorage.getItem(key);
  if (existing) return existing;
  const value = crypto.randomUUID();
  sessionStorage.setItem(key, value);
  return value;
}

interface CategoryOption {
  name: string;
  count: number;
}

function CategoryFilterList({
  options,
  selected,
  onSelect,
}: {
  options: CategoryOption[];
  selected: string;
  onSelect: (category: string) => void;
}) {
  return (
    <div className="public-category-list" aria-label="Categorias disponíveis">
      {options.map((option) => {
        const active = option.name === selected;
        return (
          <button
            className={`public-category-item${active ? " is-active" : ""}`}
            key={option.name}
            onClick={() => onSelect(option.name)}
            type="button"
            aria-pressed={active}
            aria-label={`Filtrar por ${option.name}: ${option.count} ${
              option.count === 1 ? "produto" : "produtos"
            }`}
          >
            <span className="public-category-item__label">
              {active ? <Check size={16} aria-hidden="true" /> : null}
              {option.name}
            </span>
            <span className="public-category-item__count" aria-hidden="true">
              {option.count}
            </span>
          </button>
        );
      })}
    </div>
  );
}

export function PublicStorefront({ slug }: { slug: string }) {
  const [data, setData] = useState<PublicStorefrontData | null>(null);
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("Todos");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [error, setError] = useState("");
  const filterTriggerRef = useRef<HTMLButtonElement>(null);
  const filterCloseRef = useRef<HTMLButtonElement>(null);
  const deferredQuery = useDeferredValue(query);
  useEffect(() => {
    void loadPublicStorefront(slug)
      .then(setData)
      .catch(() => setError("Esta vitrine não está disponível."));
  }, [slug]);
  const categories = useMemo(() => {
    const counts = new Map<string, number>();
    for (const product of data?.products ?? []) {
      if (!product.category) continue;
      counts.set(product.category, (counts.get(product.category) ?? 0) + 1);
    }
    return [
      { name: "Todos", count: data?.products.length ?? 0 },
      ...Array.from(counts, ([name, count]) => ({ name, count })).toSorted(
        (a, b) => a.name.localeCompare(b.name, "pt-BR"),
      ),
    ];
  }, [data?.products]);
  const hasCategoryFilters = categories.length > 2;
  useEffect(() => {
    if (!filtersOpen) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    filterCloseRef.current?.focus();
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setFiltersOpen(false);
        requestAnimationFrame(() => filterTriggerRef.current?.focus());
        return;
      }
      if (event.key !== "Tab") return;
      const drawer = filterCloseRef.current?.closest(".public-filter-drawer");
      const controls = Array.from(
        drawer?.querySelectorAll<HTMLElement>(
          'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
        ) ?? [],
      );
      const first = controls.at(0);
      const last = controls.at(-1);
      if (!first || !last) return;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    const desktop = window.matchMedia("(min-width: 861px)");
    const onViewportChange = () => {
      if (desktop.matches) setFiltersOpen(false);
    };
    document.addEventListener("keydown", onKeyDown);
    desktop.addEventListener("change", onViewportChange);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", onKeyDown);
      desktop.removeEventListener("change", onViewportChange);
    };
  }, [filtersOpen]);
  const closeFilters = () => {
    setFiltersOpen(false);
    requestAnimationFrame(() => filterTriggerRef.current?.focus());
  };
  const selectCategory = (nextCategory: string, closeDrawer = false) => {
    setCategory(nextCategory);
    if (closeDrawer) closeFilters();
  };
  const products = useMemo(() => {
    const normalizedQuery = deferredQuery.trim().toLowerCase();
    return (data?.products ?? []).filter((product) => {
      const matchesQuery =
        `${product.shortCode} ${product.name} ${product.category}`
          .toLowerCase()
          .includes(normalizedQuery);
      const matchesCategory =
        category === "Todos" || product.category === category;
      return matchesQuery && matchesCategory;
    });
  }, [category, data?.products, deferredQuery]);
  const open = async (code: string, url: string) => {
    await recordPublicClick(code, sessionId()).catch(() => undefined);
    window.open(url, "_blank", "noopener,noreferrer");
  };
  if (error)
    return (
      <main className="public-error">
        <ShoppingBag size={34} />
        <h1>{error}</h1>
        <p>Confira o endereço ou volte mais tarde.</p>
      </main>
    );
  if (!data)
    return (
      <main className="public-loading">
        <span className="spinner" />
        <span>Carregando achadinhos…</span>
      </main>
    );
  const platformCount = new Set(
    data.products.map((product) => product.platformId),
  ).size;
  return (
    <main className="public-store">
      <header className="public-hero">
        <span className="public-hero__orb public-hero__orb--one" />
        <span className="public-hero__orb public-hero__orb--two" />
        <span className="public-hero__orb public-hero__orb--three" />
        <div className="public-hero__content">
          <div className="public-brand">
            <span className="brand-mark" aria-hidden="true">
              <ShoppingBag size={24} />
            </span>
            <span>
              <strong>WeSell</strong>
              <small>curadoria pessoal</small>
            </span>
          </div>
          <p className="public-kicker">
            <Sparkles size={16} aria-hidden="true" />
            Seleção da Lalas
          </p>
          <h1>{data.storefront.title}</h1>
          <p className="public-hero__bio">{data.storefront.bio}</p>
          <div className="public-trust" aria-label="Informações da vitrine">
            <span>
              <strong>{data.products.length}</strong>
              produtos escolhidos
            </span>
            <span>
              <strong>{platformCount}</strong>
              {platformCount === 1 ? "loja selecionada" : "lojas selecionadas"}
            </span>
            <span>
              <BadgeCheck size={20} aria-hidden="true" />
              links oficiais
            </span>
          </div>
          <div className="public-search-panel">
            <label className="public-search" htmlFor="storefront-search">
              <Search size={20} aria-hidden="true" />
              <span className="sr-only">Buscar produto ou código</span>
              <input
                id="storefront-search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Digite T001 ou o nome do produto"
                type="search"
              />
            </label>
            <p>
              Busque pelo código que apareceu no vídeo ou pelo nome do item.
            </p>
          </div>
        </div>
      </header>
      <section className="public-catalog" aria-labelledby="catalog-title">
        <div className="public-catalog__heading">
          <div>
            <p className="public-section-label">Minha seleção</p>
            <h2 id="catalog-title">Achados que valem o clique</h2>
          </div>
          <div className="public-catalog__heading-actions">
            <span className="public-results" aria-live="polite">
              {products.length} {products.length === 1 ? "item" : "itens"}
            </span>
            {hasCategoryFilters ? (
              <button
                className="public-category-trigger"
                onClick={() => setFiltersOpen(true)}
                type="button"
                aria-haspopup="dialog"
                aria-expanded={filtersOpen}
                aria-controls="public-category-drawer"
                ref={filterTriggerRef}
              >
                <Filter size={18} aria-hidden="true" />
                <span>{category === "Todos" ? "Categorias" : category}</span>
                <small>{categories.length - 1}</small>
              </button>
            ) : null}
          </div>
        </div>
        <div className="public-catalog__layout">
          {hasCategoryFilters ? (
            <aside
              className="public-category-panel"
              aria-label="Filtro de categorias"
            >
              <span
                className="public-category-panel__glow"
                aria-hidden="true"
              />
              <div className="public-category-panel__header">
                <span
                  className="public-category-panel__icon"
                  aria-hidden="true"
                >
                  <Layers3 size={20} />
                </span>
                <div>
                  <p>Explore por tipo</p>
                  <h3>Categorias</h3>
                </div>
              </div>
              <p className="public-category-panel__intro">
                Encontre mais rápido o tipo de achadinho que você procura.
              </p>
              <CategoryFilterList
                options={categories}
                selected={category}
                onSelect={selectCategory}
              />
              {category !== "Todos" ? (
                <button
                  className="public-category-clear"
                  onClick={() => setCategory("Todos")}
                  type="button"
                >
                  Limpar filtro
                </button>
              ) : null}
            </aside>
          ) : null}
          <div className="public-catalog__content">
            {products.length ? (
              <div
                className="public-products"
                aria-label="Produtos recomendados"
              >
                {products.map((product, index) => {
                  const platform = data.platforms.find(
                    (p) => p.id === product.platformId,
                  );
                  return (
                    <article
                      className={`public-product public-product--tone-${(index % 6) + 1}`}
                      key={product.id}
                    >
                      <div className="public-product__image">
                        <span
                          className="public-product__placeholder"
                          aria-hidden={Boolean(product.imageUrl)}
                          aria-label={
                            product.imageUrl
                              ? undefined
                              : "Imagem ainda não disponível"
                          }
                        >
                          <span className="public-product__placeholder-icon">
                            <ImageOff size={30} aria-hidden="true" />
                          </span>
                          <span>Imagem em atualização</span>
                        </span>
                        {product.imageUrl ? (
                          <img
                            src={product.imageUrl}
                            alt={product.name}
                            width="560"
                            height="420"
                            loading="lazy"
                            onError={(event) => {
                              event.currentTarget.style.display = "none";
                            }}
                          />
                        ) : null}
                        <strong>{product.shortCode}</strong>
                      </div>
                      <div className="public-product__content">
                        <div className="public-product__meta">
                          <small>{platform?.name}</small>
                          <span>{product.category}</span>
                          {separarTamanho(product.name).tamanho ? (
                            <span className="public-product__size">
                              {separarTamanho(product.name).tamanho}
                            </span>
                          ) : null}
                        </div>
                        <h2>{separarTamanho(product.name).nome}</h2>
                        {product.description ? (
                          <p>{product.description}</p>
                        ) : null}
                        {product.price !== null ? (
                          <span className="public-product__price">
                            {money(product.price, product.currency)}
                          </span>
                        ) : null}
                        <button
                          className="button button--primary"
                          onClick={() =>
                            open(product.shortCode, product.affiliateUrl)
                          }
                        >
                          Ver na {platform?.name ?? "loja"}
                          <ArrowUpRight size={18} aria-hidden="true" />
                        </button>
                      </div>
                    </article>
                  );
                })}
              </div>
            ) : (
              <div className="public-empty">
                <Search size={26} aria-hidden="true" />
                <strong>Nenhum achadinho apareceu por aqui.</strong>
                <span>Tente outro nome, código ou escolha “Todos”.</span>
              </div>
            )}
          </div>
        </div>
        {filtersOpen && hasCategoryFilters ? (
          <>
            <button
              className="public-filter-scrim"
              onClick={closeFilters}
              type="button"
              aria-label="Fechar filtro de categorias"
            />
            <aside
              id="public-category-drawer"
              className="public-filter-drawer"
              role="dialog"
              aria-modal="true"
              aria-labelledby="mobile-filter-title"
            >
              <div className="public-filter-drawer__header">
                <div>
                  <p>Explore por tipo</p>
                  <h2 id="mobile-filter-title">Filtrar categorias</h2>
                </div>
                <button
                  className="public-filter-drawer__close"
                  onClick={closeFilters}
                  type="button"
                  aria-label="Fechar categorias"
                  ref={filterCloseRef}
                >
                  <X size={21} aria-hidden="true" />
                </button>
              </div>
              <p className="public-filter-drawer__intro">
                Escolha uma categoria para ver só os achadinhos dela.
              </p>
              <CategoryFilterList
                options={categories}
                selected={category}
                onSelect={(nextCategory) => selectCategory(nextCategory, true)}
              />
            </aside>
          </>
        ) : null}
      </section>
      <footer className="public-footer">
        <div>
          <span className="brand-mark" aria-hidden="true">
            <ShoppingBag size={20} />
          </span>
          <span>
            <strong>WeSell</strong>
            <small>Escolhido por mim. Encontrado por você.</small>
          </span>
        </div>
        <p>
          <BadgeCheck size={17} aria-hidden="true" />
          {data.storefront.disclosure}
        </p>
      </footer>
    </main>
  );
}
